import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import kitAxiosInstance from "../lib/kitAxios";

const OPEN_STATUSES = ["new", "triaged", "in-progress", "waiting"];
const STATUS_CONFIG = [
  { key: "new", label: "New", color: "#64748B" },
  { key: "triaged", label: "Triaged", color: "#0EA5E9" },
  { key: "in-progress", label: "In progress", color: "#F59E0B" },
  { key: "waiting", label: "Waiting", color: "#FDBA74" },
  { key: "resolved", label: "Resolved", color: "#059669" },
  { key: "closed", label: "Closed", color: "#DC2626" },
];
const CATEGORY_CONFIG = [
  { key: "bug", label: "Bug", color: "#EF4444" },
  { key: "feature", label: "Feature", color: "#8B5CF6" },
  { key: "question", label: "Question", color: "#3B82F6" },
  { key: "incident", label: "Incident", color: "#F97316" },
];

const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : "-");

const getDateKey = (date) => {
  const value = new Date(date);
  return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10);
};

const getLastSevenDays = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (6 - index));
    return {
      key: getDateKey(date),
      label: date.toLocaleDateString(undefined, { weekday: "short" }),
    };
  });
};

function KpiCard({ value, label, note, noteClass = "text-slate-400" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-2xl font-medium text-slate-700">{value}</div>
      <div className="mt-1 text-[10px] text-slate-400">{label}</div>
      {note && <div className={`mt-2 text-[9px] ${noteClass}`}>{note}</div>}
    </div>
  );
}

function LineChart({ points, labels }) {
  const width = 760;
  const height = 180;
  const padding = { top: 16, right: 18, bottom: 28, left: 18 };
  const maxValue = Math.max(1, ...points.flatMap((point) => [point.created, point.resolved]));
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const getPoint = (value, index) => {
    const x = padding.left + (chartWidth * index) / (points.length - 1);
    const y = padding.top + chartHeight - (value / maxValue) * chartHeight;
    return `${x},${y}`;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-48 w-full" role="img" aria-label="Created and resolved tickets over the last 7 days">
      {[0, 1, 2, 3].map((line) => {
        const y = padding.top + (chartHeight * line) / 3;
        return <line key={line} x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="#E2E8F0" strokeDasharray="2 4" />;
      })}
      <polyline points={points.map((point, index) => getPoint(point.created, index)).join(" ")} fill="none" stroke="#2395D9" strokeWidth="2.5" />
      <polyline points={points.map((point, index) => getPoint(point.resolved, index)).join(" ")} fill="none" stroke="#059669" strokeWidth="2.5" />
      {points.map((point, index) => {
        const x = padding.left + (chartWidth * index) / (points.length - 1);
        return <text key={point.key} x={x} y={height - 8} textAnchor="middle" className="fill-slate-400 text-[10px]">{labels[index]}</text>;
      })}
    </svg>
  );
}

function DonutChart({ items, total }) {
  const radius = 48;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
      <div className="relative h-32 w-32 shrink-0">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" role="img" aria-label="Ticket distribution chart">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#E2E8F0" strokeWidth="16" />
          {items.map((item) => {
            const value = total ? (item.value / total) * circumference : 0;
            const segment = (
              <circle
                key={item.key}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth="16"
                strokeDasharray={`${value} ${circumference - value}`}
                strokeDashoffset={-offset}
              />
            );
            offset += value;
            return segment;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-medium text-slate-700">{total}</span>
          <span className="text-[9px] text-slate-400">tickets</span>
        </div>
      </div>
      <div className="grid gap-2 text-[10px] text-slate-500">
        {items.map((item) => (
          <div key={item.key} className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
            <span>{item.label}</span>
            <span className="text-slate-400">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupportDashboard() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadTickets = async () => {
      try {
        const { data } = await kitAxiosInstance.get("/tickets", {
          params: { page: 1, limit: 100 },
        });
        setTickets(data.tickets || []);
      } catch {
        setError("Unable to load ticket metrics.");
      } finally {
        setIsLoading(false);
      }
    };

    loadTickets();
  }, []);

  const metrics = useMemo(() => {
    const openTickets = tickets.filter((ticket) => OPEN_STATUSES.includes(ticket.status));
    const resolvedTickets = tickets.filter((ticket) => ticket.status === "resolved");
    const openBugs = openTickets.filter((ticket) => ticket.category === "bug").length;
    const resolvedDurations = resolvedTickets
      .filter((ticket) => ticket.createdAt && ticket.resolvedAt)
      .map((ticket) => new Date(ticket.resolvedAt).getTime() - new Date(ticket.createdAt).getTime())
      .filter((duration) => duration >= 0);
    const averageResolution = resolvedDurations.length
      ? resolvedDurations.reduce((sum, duration) => sum + duration, 0) / resolvedDurations.length / 86400000
      : null;
    const days = getLastSevenDays();
    const trend = days.map((day) => ({
      ...day,
      created: tickets.filter((ticket) => getDateKey(ticket.createdAt) === day.key).length,
      resolved: tickets.filter((ticket) => getDateKey(ticket.resolvedAt) === day.key).length,
    }));
    const statuses = STATUS_CONFIG.map((status) => ({ ...status, value: tickets.filter((ticket) => ticket.status === status.key).length }));
    const categories = CATEGORY_CONFIG.map((category) => ({ ...category, value: tickets.filter((ticket) => ticket.category === category.key).length }));

    return { openTickets, resolvedTickets, openBugs, averageResolution, trend, statuses, categories };
  }, [tickets]);

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-base font-medium text-slate-700">Support dashboard</h1>
          <p className="mt-0.5 text-xs text-slate-400">Monitor support health across the AIZO Suite.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/tickets")}
          className="rounded-md bg-indigo-500 px-3 py-2 text-[10px] font-medium text-white hover:bg-indigo-600"
        >
          Manage tickets
        </button>
      </div>

      {isLoading && <p className="text-xs text-slate-400">Loading support metrics...</p>}
      {error && <p className="text-xs text-rose-500">{error}</p>}

      {!isLoading && !error && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard value={metrics.openTickets.length} label="Open tickets" />
            <KpiCard value={metrics.resolvedTickets.length} label="Resolved" />
            <KpiCard value={metrics.openBugs} label="Open bugs" note={metrics.openBugs ? "Needs attention" : "No open bugs"} noteClass={metrics.openBugs ? "text-amber-500" : "text-emerald-600"} />
            <KpiCard value={metrics.averageResolution === null ? "—" : `${metrics.averageResolution.toFixed(1)}d`} label="Avg. resolution" />
          </div>

          <section className="mt-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-xs font-medium text-slate-600">Created vs resolved · last 7 days</h2>
              <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-500" />Created</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-600" />Resolved</span>
              </div>
            </div>
            <LineChart points={metrics.trend} labels={metrics.trend.map((point) => point.label)} />
          </section>

          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-xs font-medium text-slate-600">By status</h2>
              <DonutChart items={metrics.statuses} total={tickets.length} />
            </section>
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-xs font-medium text-slate-600">By type</h2>
              <DonutChart items={metrics.categories} total={metrics.categories.reduce((sum, item) => sum + item.value, 0)} />
            </section>
          </div>
        </>
      )}
    </div>
  );
}

export default SupportDashboard;
